import { layout, prepare } from '@chenglou/pretext'

const rem = document?.documentElement 
  ? parseInt(getComputedStyle(document?.documentElement)?.fontSize) 
  : 16

const sizeToPx = (size) => rem/2*size;

const pxToSize = (px) => px/(rem/2);

const viewportWidthPx = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;

// TODO: Make the font-size dynamic since the 15.5px is being set statically
//       We need a single source of tuth for it somewhere
const pretextFont = `${15}px Roboto`
const pretextOptions = { letterSpacing: 0.35, whiteSpace: 'pre-wrap' }

const defaults = {
  postTotal: {
    heightInPx: null,
    heightInSize: null,
  },
  container: {
    paddingTop: 1.25,
    paddingLeft: 1,
    paddingRight: 0.5,
  },
  leftPanel: {
    width: 6,
    avatar: 5,
    paddingRight: 1,
  },
  rightPanel: {
    height: null,
    width: null,
    heading: {
      height: null,
      titleHeight: 2.5,
      replyHandleHeight: 0,
    },
    body: {
      // Height of the total body with padding
      height: null,
      // Height of the rendered text content
      textContentHeight: null,
      lineHeight: 2.5,
      // paddingTop: 0.5,
      paddingTop: 0.15,
      // paddingBottom: 1.5,
      // paddingBottom: 1.25,
      paddingBottom: 0.75,
    },
    media: {
      height: null,
      // NOTE: We changed this from 45. See how 55 feels, and don't be afraid to go back to 45size
      maxHeight: 55,
    },
    actionBar: {
      height: null,
      paddingTop: 1.25,
      paddingBottom: 1.35,
      contentHeight: 2.5,
    }
  },
  repost: {
    text: {
      height: 3.5,
    },
    icon: {
      height: 3.5,
    }
  },
}

class PostDimensions {

  constructor({ post, defaults, rem, viewportWidthPx }) {
    this.viewportWidthPx = viewportWidthPx
    this.dimensions = structuredClone(defaults)
    this.post = post
    this.rem = rem
    this.viewportWidth = this.pxToSize(viewportWidthPx)
    this.setDynamicDimensions()
  }

  static insertDimensions({ post }) {
    const result = new this({ post, defaults, rem, viewportWidthPx })
    post.dimensions = result?.dimensions || {}
  }

  static insertDimensionsAll(posts=[]) {
    posts.forEach((post) => this.insertDimensions({ post }))
  }

  setDynamicDimensions() {
    // Set Right Panel
    this.generateRightPanelWidth()
    // Set Heading
    this.generateHeadingHeight()
    // Set Body
    this.generateBodyHeight()
    // Set Media
    this.generateMediaHeight()
    // Set ActionBar
    this.generateActionBarHeight()
    // Get right panel height
    this.calculateRightPanelHeight()
    // Get post totals
    this.calculateTotalHeights()
    // NOTE: You'll want the final dimensions in your size format for rendering, but you do need to save some details in px to set static height
  }

  generateRightPanelWidth() {
    const { dimensions, viewportWidth } = this
    const { rightPanel, container, leftPanel } = dimensions
    rightPanel.width = viewportWidth - (container.paddingLeft + container.paddingRight +  leftPanel.width)
  }

  generateHeadingHeight() {
    const { heading } = this.dimensions.rightPanel
    const { titleHeight, replyHandleHeight } = heading
    if (this.post.displayType === 'reply_bottom_display') {
      // TODO: We would like to put the reply handle back in for reply bottom only posts
      heading.height = titleHeight + replyHandleHeight
    } else {
      heading.height = 2.5
    }
  }
   
  generateBodyHeight() {
    const { post } = this
    const { body, width } = this.dimensions.rightPanel

    // Pretext Logic for body textContent
    const textContent = this.stripHtml(post.body)
    if (textContent) {
      const prepared = prepare(textContent, pretextFont, pretextOptions)
      const { height } = layout(prepared, this.sizeToPx(width), this.sizeToPx(body.lineHeight))
      // default to single line if we can't get the height for some reason
      body.textContentHeight = height ? this.pxToSize(height) : body.lineHeight
    } else {
      // If there's no textContent, the height should be zero
      body.textContentHeight = 0
    }
    body.height = body.textContentHeight + body.paddingTop + body.paddingBottom
  }

  generateMediaHeight() {
    const { post } = this
    const { media, width } = this.dimensions.rightPanel
    const postMedia = post?.media?.[0]
    if (postMedia?.uuid && postMedia?.height && postMedia?.width) {
      // We're doing these converstions to have a flat px number to render when we convert back from size to px later
      media.height = this.pxToSize(Math.floor(postMedia.height/postMedia.width * this.sizeToPx(width)))
      // If the height of the media is greater than the max height, revert to the max height
      if (media.height > media.maxHeight) {
        media.height = media.maxHeight
      }
    } else {
      media.height = 0
    }
  }

  generateActionBarHeight() {
    const { post } = this
    const { actionBar, media } = this.dimensions.rightPanel

    // If there's no media, reduce the top padding so the
    // action bars aren't so far away from the body text
    if (!media.height) {
      actionBar.paddingTop = 0
    }
    if (post.displayType === 'reply_top_display') {
      actionBar.paddingBottom = 0.5
    }

    actionBar.height = actionBar.contentHeight +
      actionBar.paddingTop +
      actionBar.paddingBottom
  }

  calculateRightPanelHeight() {
    const { rightPanel } = this.dimensions
    const { heading, body, media, actionBar } = rightPanel
    rightPanel.height = (heading.height || 0) + (body.height || 0) + (media.height || 0) + (actionBar.height || 0)
  }

  calculateTotalHeights() {
    const { post } = this
    const { postTotal, container, rightPanel, repost } = this.dimensions
    postTotal.heightInSize = container.paddingTop + (rightPanel.height || 0)
    if (post.displayType === 'repost_display') {
      postTotal.heightInSize += repost.text.height
    }
    postTotal.heightInPx = this.sizeToPx(postTotal.heightInSize)
  }

  sizeToPx(size) {
    return this.rem/2*size
  }

  pxToSize(px) {
    return px/(this.rem/2)
  }

  stripHtml(htmlString) {
    const newlineInserted = htmlString
        .replace(/<br\s*\/?>/gi, '\n')                // Convert <br> to newlines
        .replace(/<\/(p|div|h[1-6])>/gi, '\n')       // Convert closing block tags to newlines
        .replace(/<[^>]+>/g, '');

    const doc = new DOMParser().parseFromString(newlineInserted, 'text/html');
    return doc.body.textContent || "";
  }
}

export { PostDimensions, rem, pxToSize, sizeToPx, viewportWidthPx, }
